import React, { useMemo } from "react"
import SEO from "@components/seo"
import PageHeader from "@components/page-header"
import { isVisibleIn } from "@utils/region"
import ProductFilters from "@components/product/filters"
import ContentBuilder from "@src/components/content-builder"

const CollectionsPage = ({ pageContext, location }) => {
  const {
    pageEntry: pageContent,
    productEntries: { nodes: products },
    collection,
    productGroups: { nodes: allCollections },
  } = pageContext

  const params = new URLSearchParams(location.search);

  const contentBuilderBlocks = collection?.contentBuilder || pageContent?.contentBuilder || pageContent?.[0]?.contentBuilder;

  const matchCollectionIds = (first, second) => {
    return first?.replace('drafts.', '') === second?.replace('drafts.', '');
  }

  const selectedProductGroup = useMemo(() => {
    return {
      ...collection,
      products: products?.filter(
        product =>
          product.showInProductListing &&
          isVisibleIn(product.visibilityRegions) &&
          (product.productGroups || []).some(
            group => matchCollectionIds(group?._id, collection?._id)
          )
      ),
      subcategories: collection?.subcategories?.map(subcategory => {
        return {
          ...subcategory,
          products: products.filter(
            product =>
              product.showInProductListing &&
              isVisibleIn(product.visibilityRegions) &&
              (product.productGroups || []).some(
                group => matchCollectionIds(group?._id, subcategory?._id)
              )
          ),
        }
      }),
    }
  })

  const allProductGroups = useMemo(() => {
    const subcategoryIds = []

    allCollections.forEach(collection => {
      collection?.subcategories?.forEach(subcategory => {
        if (!subcategoryIds.includes(subcategory?._id)) {
          subcategoryIds.push(subcategory?._id)
        }
      })
    })

    return allCollections
      .filter(productGroup => productGroup.hide != true)
      .map(productGroup => ({
        ...productGroup,
        products: products.filter(
          product =>
            product.showInProductListing &&
            isVisibleIn(product.visibilityRegions) &&
            (product.productGroups || []).some(
              group => matchCollectionIds(group?._id, productGroup?._id)
            )
        ),
        subcategories: productGroup?.subcategories?.filter(subcategory => subcategory.hide != true).map(subcategory => {
          return {
            ...subcategory,
            products: products.filter(
              product =>
                product.showInProductListing &&
                isVisibleIn(product.visibilityRegions) &&
                (product.productGroups || []).some(
                  group => matchCollectionIds(group?._id, subcategory?._id)
                )
            ),
          }
        }),
      }))
      .filter(
        productGroup =>
          productGroup.products.length > 0 &&
          !subcategoryIds.includes(productGroup?._id)
      )
  }, [allCollections, products])

  const allSelectedProducts = useMemo(() => {
    let filteredProducts = products.filter(product => {
      if (
        product.showInProductListing &&
        isVisibleIn(product.visibilityRegions)
      ) {
        return product
      }
    })

    if (selectedProductGroup?._id) {
      filteredProducts = filteredProducts.filter(product =>
        (product.productGroups || []).some(
          group => matchCollectionIds(group?._id, selectedProductGroup?._id)
        )
      )
    }

    return filteredProducts
  }, [products])

  const titleInner = (selectedProductGroup.title || pageContent[0]?.title)?.replace(/_/g, ' ');

  const headerData = {
    title: titleInner,
    headingElement: 'h1',
    intro: selectedProductGroup?.description || pageContent[0]?.pageIntro,
    bgImage: selectedProductGroup?.mainImage || pageContent[0]?.mainImage,
    mobileBgImage: selectedProductGroup?.mobileCoverImage || pageContent[0]?.mobileImage,
    style: { intro: '!font-normal sm:!font-medium !leading-tight sm:leading-5 text-sm sm:text-lg' }
  };

  return (
    <>
      <SEO {...{ document: pageContent }} />
      <PageHeader {...headerData} />
      <ProductFilters
        {...{
          allProductGroups,
          selectedProductGroup,
          allSelectedProducts: allSelectedProducts,
          params,
        }}
      />
      {contentBuilderBlocks && <ContentBuilder blocks={contentBuilderBlocks} />}
    </>
  )
}

export default CollectionsPage
